/**
 * O formulário da preventiva, na caixa de diálogo do SM.
 *
 * Papel: reproduz `#prevModalOverlay` (l. 673–707) e o que `openPrevModal`,
 * `savePrevRecord` e `deletePrevRecord` fazem (l. 1105–1157): os campos nas
 * sete linhas da origem, "Cancelar", "Excluir" só na edição e "Salvar".
 *
 * Como usar: quem monta passa `preventiva` para editar ou `null` para criar, dá
 * ao componente uma `key` por registro e o desmonta para fechar. "#",
 * periodicidade e responsável aceitam qualquer texto; a periodicidade sugere as
 * nove da origem pelo `datalist`, Mensal incluída. Nada compara as duas visitas:
 * a próxima pode ser anterior à última.
 *
 * Gravar e excluir seguem as regras do formulário da corretiva: sem `<form>`,
 * Enter não grava; os dois botões ficam desabilitados até a resposta; a falha
 * deixa a caixa aberta e diz "Não foi possível salvar." ou "Não foi possível
 * excluir.".
 */
import { type Preventiva, novaPreventivaSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useNotificacao } from '@/shared/embutido';
import { useExcluirPreventiva, useSalvarPreventiva } from '../hooks/usar-manutencoes';
import { PERIODICIDADES_SUGERIDAS } from '../regras';
import { ORDEM_DOS_STATUS_PREVENTIVA, ROTULOS_STATUS_PREVENTIVA } from '../rotulos';
import { PREVENTIVA_EM_BRANCO, type PreventivaNoFormulario } from '../types';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import {
  CLASSE_DA_AREA_DE_TEXTO,
  CLASSE_DO_CONTROLE,
  CampoDoFormulario,
  LinhaDupla,
} from './kit/campo-do-formulario';

interface FormularioPreventivaProps {
  /** A preventiva a editar, ou `null` para criar. */
  preventiva: Preventiva | null;
  /** A casa da tela, dona da preventiva. */
  residenciaId: string;
  /** Fecha a caixa, desmontando o formulário. */
  onFechar: () => void;
}

/**
 * O custo como a origem o lê do campo.
 *
 * @param valor - O texto do `input type="number"`.
 * @returns `Number` do texto, ou `null` para o campo vazio.
 */
function custoDoCampo(valor: string): number | null {
  return valor === '' ? null : Number(valor);
}

/**
 * O formulário de uma preventiva.
 *
 * @param preventiva - O registro a editar, ou `null`.
 * @param residenciaId - A casa da tela.
 * @param onFechar - Chamado para fechar, depois de gravar ou excluir, ou pelo
 *   "✕" e pelo "Cancelar".
 * @returns A caixa com os campos.
 */
export function FormularioPreventiva({
  preventiva,
  residenciaId,
  onFechar,
}: FormularioPreventivaProps) {
  const id = useId();
  const notificar = useNotificacao();
  const salvar = useSalvarPreventiva();
  const excluir = useExcluirPreventiva();
  const [ocupado, setOcupado] = useState(false);
  // "Cancelar" e "✕" seguem ativos durante a gravação; se ela volta depois do
  // formulário desmontado, `onFechar` fecharia a caixa que a pessoa abriu depois.
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const form = useForm<PreventivaNoFormulario>({
    resolver: zodResolver(novaPreventivaSchema),
    defaultValues:
      preventiva === null
        ? { ...PREVENTIVA_EM_BRANCO, residenciaId }
        : {
            residenciaId,
            numero: preventiva.numero ?? '',
            elemento: preventiva.elemento ?? '',
            atividade: preventiva.atividade ?? '',
            status: preventiva.status,
            periodicidade: preventiva.periodicidade ?? '',
            empresa: preventiva.empresa ?? '',
            responsavel: preventiva.responsavel ?? '',
            ultimaVisita: preventiva.ultimaVisita ?? '',
            proximaVisita: preventiva.proximaVisita ?? '',
            custo: preventiva.custo ?? '',
            observacao: preventiva.observacao ?? '',
          },
  });

  /**
   * Grava o que passou pelo resolver.
   *
   * @param dados - Os valores do formulário, já normalizados pelo Zod.
   */
  async function gravar(dados: PreventivaNoFormulario) {
    try {
      await salvar.mutateAsync({
        id: preventiva?.id ?? null,
        dados: novaPreventivaSchema.parse(dados),
      });
      notificar('✓ Registro salvo');
      if (montado.current) onFechar();
    } catch {
      notificar('Não foi possível salvar.', false);
      setOcupado(false);
    }
  }

  /** Avisa a recusa do resolver, sem fechar a caixa. */
  function recusar() {
    notificar('Não foi possível salvar.', false);
    setOcupado(false);
  }

  /** Grava, travando os dois botões até a resposta. */
  function handleSalvar() {
    setOcupado(true);
    void form.handleSubmit(gravar, recusar)();
  }

  /** Exclui sem perguntar, como `deletePrevRecord`. */
  async function handleExcluir() {
    if (preventiva === null) return;
    setOcupado(true);
    try {
      await excluir.mutateAsync(preventiva.id);
      notificar('✓ Registro excluído');
      if (montado.current) onFechar();
    } catch {
      notificar('Não foi possível excluir.', false);
      setOcupado(false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo={
        preventiva === null ? 'Novo registro — Preventivas' : 'Editar registro — Preventivas'
      }
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          {preventiva !== null && (
            <Botao variante="perigo" disabled={ocupado} onClick={() => void handleExcluir()}>
              Excluir
            </Botao>
          )}
          <Botao variante="primario" disabled={ocupado} onClick={handleSalvar}>
            Salvar
          </Botao>
        </>
      }
    >
      <LinhaDupla>
        <CampoDoFormulario rotulo="#" id={`${id}-numero`}>
          <input
            id={`${id}-numero`}
            type="text"
            className={CLASSE_DO_CONTROLE}
            {...form.register('numero')}
          />
        </CampoDoFormulario>
        <CampoDoFormulario rotulo="Status" id={`${id}-status`}>
          <select
            id={`${id}-status`}
            className={CLASSE_DO_CONTROLE}
            {...form.register('status')}
          >
            {ORDEM_DOS_STATUS_PREVENTIVA.map((status) => (
              <option key={status} value={status}>
                {ROTULOS_STATUS_PREVENTIVA[status]}
              </option>
            ))}
          </select>
        </CampoDoFormulario>
      </LinhaDupla>
      <CampoDoFormulario rotulo="Elemento / Componente" id={`${id}-elemento`}>
        <input
          id={`${id}-elemento`}
          type="text"
          className={CLASSE_DO_CONTROLE}
          {...form.register('elemento')}
        />
      </CampoDoFormulario>
      <CampoDoFormulario rotulo="Atividade" id={`${id}-atividade`}>
        <textarea
          id={`${id}-atividade`}
          className={CLASSE_DA_AREA_DE_TEXTO}
          {...form.register('atividade')}
        />
      </CampoDoFormulario>
      <LinhaDupla>
        <CampoDoFormulario rotulo="Periodicidade" id={`${id}-periodicidade`}>
          <input
            id={`${id}-periodicidade`}
            type="text"
            list={`${id}-periodicidades`}
            className={CLASSE_DO_CONTROLE}
            {...form.register('periodicidade')}
          />
          <datalist id={`${id}-periodicidades`}>
            {PERIODICIDADES_SUGERIDAS.map((periodicidade) => (
              <option key={periodicidade} value={periodicidade} />
            ))}
          </datalist>
        </CampoDoFormulario>
        <CampoDoFormulario rotulo="Empresa" id={`${id}-empresa`}>
          <input
            id={`${id}-empresa`}
            type="text"
            className={CLASSE_DO_CONTROLE}
            {...form.register('empresa')}
          />
        </CampoDoFormulario>
      </LinhaDupla>
      <LinhaDupla>
        <CampoDoFormulario rotulo="Responsável" id={`${id}-responsavel`}>
          <input
            id={`${id}-responsavel`}
            type="text"
            className={CLASSE_DO_CONTROLE}
            {...form.register('responsavel')}
          />
        </CampoDoFormulario>
        <CampoDoFormulario rotulo="Custo (R$)" id={`${id}-custo`}>
          <input
            id={`${id}-custo`}
            type="number"
            step="0.01"
            className={CLASSE_DO_CONTROLE}
            {...form.register('custo', { setValueAs: custoDoCampo })}
          />
        </CampoDoFormulario>
      </LinhaDupla>
      <LinhaDupla>
        <CampoDoFormulario rotulo="Última visita" id={`${id}-ultima`}>
          <input
            id={`${id}-ultima`}
            type="date"
            className={CLASSE_DO_CONTROLE}
            {...form.register('ultimaVisita')}
          />
        </CampoDoFormulario>
        <CampoDoFormulario rotulo="Próxima visita (previsão)" id={`${id}-proxima`}>
          <input
            id={`${id}-proxima`}
            type="date"
            className={CLASSE_DO_CONTROLE}
            {...form.register('proximaVisita')}
          />
        </CampoDoFormulario>
      </LinhaDupla>
      <CampoDoFormulario rotulo="Observações" id={`${id}-observacao`}>
        <textarea
          id={`${id}-observacao`}
          className={CLASSE_DA_AREA_DE_TEXTO}
          {...form.register('observacao')}
        />
      </CampoDoFormulario>
    </CaixaDeDialogo>
  );
}
